// src/components/PendingChecklistToast.js
//
// Displays an amber toast at the bottom of the screen when the logged-in user
// has pending or overdue checklist instances assigned to them for today.
// Dismissing snoozes the toast for 1 hour (stored in localStorage).
// Re-checks on mount and whenever the browser tab regains focus.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import sqfService from '../services/sqfService';

const SNOOZE_MS = 60 * 60 * 1000; // 1 hour

function getUserId() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded?.id ?? decoded?.userId ?? null;
  } catch {
    return null;
  }
}

function snoozedKey(userId) {
  return `sqf_pending_dismissed_${userId}`;
}

function isSnoozed(userId) {
  if (!userId) return true;
  const raw = localStorage.getItem(snoozedKey(userId));
  if (!raw) return false;
  return Date.now() - parseInt(raw, 10) < SNOOZE_MS;
}

function snooze(userId) {
  if (!userId) return;
  localStorage.setItem(snoozedKey(userId), String(Date.now()));
}

export default function PendingChecklistToast() {
  const [instances, setInstances] = useState([]);
  const [visible, setVisible] = useState(false);
  const userId = useRef(getUserId());

  const check = useCallback(async () => {
    const uid = userId.current;
    if (!uid || isSnoozed(uid)) return;
    try {
      // No date filter — catch overdue from previous days too
      const pending = await sqfService.getInstances({ assignedToMe: true, status: 'pending' });
      const overdue = await sqfService.getInstances({ assignedToMe: true, status: 'overdue' });
      const all = [...(pending || []), ...(overdue || [])];
      if (all.length > 0) {
        setInstances(all);
        setVisible(true);
      } else {
        setVisible(false);
      }
    } catch (e) {
      console.warn('[PendingChecklistToast] check failed:', e?.message);
    }
  }, []);

  useEffect(() => {
    check();
    const onFocus = () => check();
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [check]);

  const handleDismiss = () => {
    snooze(userId.current);
    setVisible(false);
  };

  if (!visible || instances.length === 0) return null;

  const names = instances.map(i => i.Template?.name).filter(Boolean);
  const uniqueNames = [...new Set(names)];
  const overdue = instances.some(i => i.status === 'overdue');

  return (
    <div
      className="app-no-print"
      style={{
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        minWidth: 300,
        maxWidth: 520,
        width: 'calc(100vw - 32px)',
      }}
    >
      <div className={`alert ${overdue ? 'alert-danger' : 'alert-warning'} shadow d-flex align-items-start gap-3 mb-0 py-2 px-3`}>
        <div className="flex-grow-1" style={{ fontSize: 13 }}>
          <strong>{overdue ? '⚠ Overdue checklist' : '📋 Pending checklist'}{instances.length > 1 ? 's' : ''}:</strong>{' '}
          {uniqueNames.slice(0, 3).join(', ')}
          {uniqueNames.length > 3 && ` +${uniqueNames.length - 3} more`}
          <div className="mt-1">
            <Link to="/sqf/checklists" className={`btn btn-sm ${overdue ? 'btn-danger' : 'btn-warning'} me-2`} onClick={handleDismiss}>
              Complete Now →
            </Link>
            <button className="btn btn-sm btn-outline-secondary" onClick={handleDismiss} style={{ fontSize: 11 }}>
              Remind me in 1 hr
            </button>
          </div>
        </div>
        <button type="button" className="btn-close btn-sm mt-1" onClick={handleDismiss} />
      </div>
    </div>
  );
}
